// Procedural weather sounds with the Web Audio API (no audio files to download):
//   • rain: a soft hiss of rain on glass plus individual drops "ticking" on the roof
//   • thunder: a low rumble shortly after each lightning flash
//   • wind: a gentle, slowly swelling whoosh
// Everything follows the live weather state (`weatherRef.current`), so intensity changes are smooth.

const MASTER_VOLUME = 0.32; // deliberately mild

const makeNoiseBuffer = (ctx, seconds) => {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  // Slightly "pink" noise: warmer than white noise, sounds more like real rain.
  let b0 = 0, b1 = 0, b2 = 0;
  for (let i = 0; i < length; i++) {
    const white = Math.random() * 2 - 1;
    b0 = 0.99765 * b0 + white * 0.099046;
    b1 = 0.963 * b1 + white * 0.2965164;
    b2 = 0.57 * b2 + white * 1.0526913;
    data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.18;
  }
  return buffer;
};

const loopNoise = (ctx, buffer) => {
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.loop = true;
  src.start(0, Math.random() * buffer.duration);
  return src;
};

export function createWeatherSound(weatherRef, { enabled: startEnabled = true, suspended: startSuspended = false } = {}) {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  const ctx = new AudioCtx();
  const noise = makeNoiseBuffer(ctx, 4);
  const dropBuffer = makeNoiseBuffer(ctx, 0.05);

  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(ctx.destination);

  // Rain hiss: band-limited noise, like many tiny drops on glass.
  const rainGain = ctx.createGain();
  rainGain.gain.value = 0;
  const rainHP = ctx.createBiquadFilter();
  rainHP.type = 'highpass';
  rainHP.frequency.value = 500;
  const rainLP = ctx.createBiquadFilter();
  rainLP.type = 'lowpass';
  rainLP.frequency.value = 6000;
  loopNoise(ctx, noise).connect(rainHP);
  rainHP.connect(rainLP).connect(rainGain).connect(master);

  // Wind: low noise through a slowly moving band-pass filter.
  const windGain = ctx.createGain();
  windGain.gain.value = 0;
  const windBP = ctx.createBiquadFilter();
  windBP.type = 'bandpass';
  windBP.frequency.value = 400;
  windBP.Q.value = 0.8;
  loopNoise(ctx, noise).connect(windBP);
  windBP.connect(windGain).connect(master);

  // Individual drops hitting the glass roof: short, bright "tik" sounds.
  const playDrop = (when, loudness) => {
    const src = ctx.createBufferSource();
    src.buffer = dropBuffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2500 + Math.random() * 4500;
    bp.Q.value = 6 + Math.random() * 8;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(loudness, when + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.03 + Math.random() * 0.04);
    src.connect(bp).connect(g).connect(master);
    src.start(when);
    src.stop(when + 0.1);
  };

  // Thunder: a long, low rumble with a soft attack.
  const playThunder = (when, strength) => {
    const src = ctx.createBufferSource();
    src.buffer = noise;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.setValueAtTime(220, when);
    lp.frequency.exponentialRampToValueAtTime(70, when + 3);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(1.6 * strength, when + 0.25);
    g.gain.exponentialRampToValueAtTime(0.001, when + 3.5 + Math.random() * 1.5);
    src.connect(lp).connect(g).connect(master);
    src.start(when, Math.random() * 2);
    src.stop(when + 5.5);
  };

  let enabled = startEnabled;
  let suspended = startSuspended;
  let fadeIn = 0.12; // the very first fade-in is quick, so sound starts right after the first tap
  let lastStrikes = (weatherRef.current && weatherRef.current.strikes) || 0;
  let dropDebt = 0;
  let lastTick = ctx.currentTime;

  const tick = () => {
    const w = weatherRef.current || {};
    const now = ctx.currentTime;
    const dt = Math.min(0.25, now - lastTick);
    lastTick = now;
    const storm = w.storm || 0;
    // Rain intensity: steady rain is a moderate patter, a storm is a downpour.
    const rain = (w.rain || 0) * (0.6 + 0.4 * storm);
    const wind = w.wind || 0;
    const smooth = 0.4; // seconds to glide to new levels

    const on = enabled && !suspended ? 1 : 0;
    master.gain.setTargetAtTime(MASTER_VOLUME * on, now, fadeIn);
    if (ctx.state === 'running' && on && master.gain.value > MASTER_VOLUME * 0.9) fadeIn = 0.5; // later changes glide gently
    // Heavier rain: louder and a little brighter.
    rainGain.gain.setTargetAtTime(rain * 0.55, now, smooth);
    rainLP.frequency.setTargetAtTime(3500 + rain * 3500, now, smooth);
    // Wind swells and fades slowly.
    const gust = 0.6 + 0.4 * Math.sin(now * 0.35) * Math.sin(now * 0.13 + 1);
    windGain.gain.setTargetAtTime(wind * 0.5 * gust, now, 0.8);
    windBP.frequency.setTargetAtTime(280 + gust * 260, now, 0.8);

    if (on && rain > 0.02) {
      // Up to ~70 drops a second in a downpour, a few in light rain.
      dropDebt += dt * (4 + rain * rain * 66);
      while (dropDebt >= 1) {
        dropDebt -= 1;
        playDrop(now + Math.random() * 0.1, (0.15 + Math.random() * 0.35) * (0.4 + rain * 0.6));
      }
    } else {
      dropDebt = 0;
    }

    // A new lightning strike → thunder arrives 0.3–1.8 s later.
    const strikes = w.strikes || 0;
    if (on && strikes !== lastStrikes) playThunder(now + 0.3 + Math.random() * 1.5, 0.6 + Math.random() * 0.4);
    lastStrikes = strikes;
  };

  const timer = setInterval(tick, 50);
  const onVisibility = () => (document.hidden ? ctx.suspend() : ctx.resume());
  document.addEventListener('visibilitychange', onVisibility);

  // iOS Safari only unlocks audio inside a tap; playing a silent sample at that moment makes sure
  // the unlock "takes" (resume() alone is sometimes ignored).
  const unlock = () => {
    if (ctx.state === 'running' || document.hidden) return;
    try {
      const src = ctx.createBufferSource();
      src.buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
      src.connect(ctx.destination);
      src.start(0);
    } catch (e) { /* ignore */ }
    ctx.resume().catch(() => {});
  };

  return {
    resume: unlock,
    isRunning: () => ctx.state === 'running',
    setEnabled: (v) => { enabled = v; },
    setSuspended: (v) => { suspended = v; },
    dispose: () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      ctx.close().catch(() => {});
    },
  };
}

export const getWeatherSoundEnabled = () => {
  try { return localStorage.getItem('gallery-weather-sound') !== 'off'; } catch (e) { return true; }
};
export const saveWeatherSoundEnabled = (on) => {
  try { localStorage.setItem('gallery-weather-sound', on ? 'on' : 'off'); } catch (e) { /* ignore */ }
};
